import React, { useCallback, useState } from 'react';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Colors } from '../../../../ui';
import { useResolve } from '../../../Container';
import { escape } from '../../../canvas/commands/ManipulationCommands';
import { TextAlignment, TextSettings } from '../../../canvas/entities/TextConventions';
import { UIField, UIFieldProps } from '../../../canvas/entities/UIField';
import { useComponentObserver } from '../../../canvas/visualization/ComponentObserver';
import { SimpleTextFieldInput } from '../../../canvas/workspace/adapters/SimpleTextFieldInput';
import { JiraSprint } from '../JiraSprint';
import { jiraSprintSetGoal } from '../JiraSprintCommands';
import { JiraOAuthRequired } from './JiraOAuthRequired';

export const JiraSprintGoalTextSettings = TextSettings.from({
    fontFamily: 'Rubik',
    fontColor: Colors.text,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.4,
    textMargin: [8, 8],
    fontStyle: 'normal',
    maxFontSize: null,
    textAlignH: TextAlignment.start,
    textAlignV: TextAlignment.start,
});

const JiraSprintGoalFieldEditor = ({ active, field }: UIFieldProps) => {

    const dispatch = useResolve(CommandDispatcher);
    const object = field.owner.cast(JiraSprint);
    useComponentObserver(object);

    const [initialValue] = useState(object.record?.goal ?? '');
    const [value, setValue] = useState(initialValue);

    const onChange = useCallback((nextValue: string) => {
        setValue(nextValue);
    }, [field]);

    const onCommit = useCallback((finalValue: string) => {
        if (finalValue === initialValue) return;
        dispatch(jiraSprintSetGoal, { targetId: object.id, goal: finalValue });
    }, [initialValue, object.id, dispatch]);

    const onReject = () => dispatch(escape);

    return <JiraOAuthRequired onReject={onReject}>
        <SimpleTextFieldInput {...{
            active,
            field,
            settings: JiraSprintGoalTextSettings,
            value,
            onChange,
            onCommit,
            placeholder: 'Sprint Goal',
            multiline: true,
        }} />
    </JiraOAuthRequired>;
};

export const JiraSprintGoalField = (owner: JiraSprint): UIField => ({
    ui: JiraSprintGoalFieldEditor,
    name: 'goal',
    bounds: owner.layout.goal,
    owner,
});
